/**
 * Product Tabs Functionality
 * Handles tab switching for product information tabs
 */

class ProductTabs {
  constructor(container) {
    this.container = container;
    this.tabButtons = container.querySelectorAll('.product-tabs__button');
    this.tabPanels = container.querySelectorAll('.product-tabs__panel');
    this.init();
  }

  init() {
    if (!this.tabButtons.length || !this.tabPanels.length) return;

    // Add click handlers to all tab buttons
    this.tabButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchTab(button.dataset.tab);
      });
    });

    // Handle keyboard navigation
    this.tabButtons.forEach((button) => {
      button.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          this.handleArrowKeyNavigation(e.key);
        } else if (e.key === 'Home' || e.key === 'End') {
          e.preventDefault();
          this.handleHomeEndNavigation(e.key);
        }
      });
    });
  }

  switchTab(tabName) {
    // Find the button and panel for this tab
    const targetButton = Array.from(this.tabButtons).find(
      (btn) => btn.dataset.tab === tabName
    );
    const sectionId = this.getSectionId();
    const expectedPanelId = `tab-${tabName}-${sectionId}`;
    const targetPanel = Array.from(this.tabPanels).find(
      (panel) => panel.id === expectedPanelId
    );

    if (!targetButton) {
      console.warn('ProductTabs: Tab button not found for', tabName);
      return;
    }
    if (!targetPanel) {
      console.warn('ProductTabs: Tab panel not found. Expected ID:', expectedPanelId, 'Available panels:', Array.from(this.tabPanels).map(p => p.id));
      return;
    }

    // Remove active state from all buttons and panels
    this.tabButtons.forEach((btn) => {
      btn.classList.remove('active');
      btn.setAttribute('aria-selected', 'false');
    });

    this.tabPanels.forEach((panel) => {
      panel.classList.remove('active');
      panel.setAttribute('hidden', '');
    });

    // Add active state to target button and panel
    targetButton.classList.add('active');
    targetButton.setAttribute('aria-selected', 'true');
    targetPanel.classList.add('active');
    targetPanel.removeAttribute('hidden');

    // Scroll to top of tab content (optional, for better UX)
    targetPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    // Dispatch custom event for any listeners
    this.container.dispatchEvent(
      new CustomEvent('tab-switched', {
        detail: { tabName, button: targetButton, panel: targetPanel },
        bubbles: true,
      })
    );
  }

  handleArrowKeyNavigation(key) {
    const activeButton = Array.from(this.tabButtons).find((btn) =>
      btn.classList.contains('active')
    );
    if (!activeButton) return;

    const currentIndex = Array.from(this.tabButtons).indexOf(activeButton);
    let newIndex;

    if (key === 'ArrowLeft') {
      newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabButtons.length - 1;
    } else if (key === 'ArrowRight') {
      newIndex = currentIndex < this.tabButtons.length - 1 ? currentIndex + 1 : 0;
    }

    if (newIndex !== undefined) {
      const newButton = this.tabButtons[newIndex];
      this.switchTab(newButton.dataset.tab);
      newButton.focus();
    }
  }

  handleHomeEndNavigation(key) {
    if (key === 'Home') {
      const firstButton = this.tabButtons[0];
      this.switchTab(firstButton.dataset.tab);
      firstButton.focus();
    } else if (key === 'End') {
      const lastButton = this.tabButtons[this.tabButtons.length - 1];
      this.switchTab(lastButton.dataset.tab);
      lastButton.focus();
    }
  }

  getSectionId() {
    // Extract section ID from container ID
    // Handle both numeric IDs and full section IDs like "template--25948948365614__main"
    const containerId = this.container.id;
    if (containerId.startsWith('ProductTabs-')) {
      return containerId.replace('ProductTabs-', '');
    }
    // Fallback: try to extract numeric ID
    const match = containerId.match(/ProductTabs-(\d+)/);
    return match ? match[1] : '';
  }
}

// Initialize tabs when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  const tabContainers = document.querySelectorAll('.product-tabs');
  tabContainers.forEach((container) => {
    new ProductTabs(container);
  });
});

// Also initialize tabs that might be loaded dynamically
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const tabContainers = document.querySelectorAll('.product-tabs');
    tabContainers.forEach((container) => {
      new ProductTabs(container);
    });
  });
} else {
  // DOM already loaded
  const tabContainers = document.querySelectorAll('.product-tabs');
  tabContainers.forEach((container) => {
    new ProductTabs(container);
  });
}
